import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { simulationApi } from '../../services/simulationApi';
import { UnifiedMapView } from './UnifiedMapView';
import { SimActorPairingQrModal } from '../SimActorPairingQrModal';
import { useSimSessionFocus } from '../../stores/useSimSessionFocus';

interface OverviewPanelProps {
  schoolIds: string[];
  theme: 'dark' | 'light';
}

export function OverviewPanel({ schoolIds, theme }: OverviewPanelProps) {
  const isLight = theme === 'light';
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState<any>(null);
  const [fixedActors, setFixedActors] = useState<any>(null);
  const [mapHeight, setMapHeight] = useState(() => {
    const saved = localStorage.getItem('ac_overview_map_height');
    return saved ? parseInt(saved, 10) : 280;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [activeActorQr, setActiveActorQr] = useState<any | null>(null);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const focusedSessionId = useSimSessionFocus((s) => s.focusedSessionId);

  const loadData = useCallback(async () => {
    if (schoolIds.length === 0) return;
    setLoading(true);
    try {
      const [over, actors] = await Promise.all([
        simulationApi.getOverviewPanel(schoolIds),
        simulationApi.getAtoresFixos(),
      ]);
      setOverviewData(over);
      setFixedActors(actors);
    } catch (e) {
      console.error('[OverviewPanel] Erro ao carregar dados consolidados:', e);
    } finally {
      setLoading(false);
    }
  }, [schoolIds]);

  const handleDisconnect = async (actorId: string) => {
    if (!window.confirm('Deseja desconectar o aparelho deste ator de simulação?')) return;
    setActionLoadingId(actorId);
    try {
      await simulationApi.disconnectDevice(actorId);
      await loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e.message || 'Erro ao desconectar dispositivo.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleSendPush = async (actorId: string) => {
    setActionLoadingId(actorId);
    try {
      await simulationApi.sendTestPush(actorId);
      toast.success('Notificação push de teste enviada com sucesso!');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e.message || 'Erro ao enviar push de teste.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCopyLink = (actorId: string) => {
    const targetUrl = `${window.location.origin}/api/v1/simulation/magic-login-redirect?actorId=${actorId}&origin=${encodeURIComponent(window.location.origin)}`;
    navigator.clipboard.writeText(targetUrl);
    toast.success('Link de login mágico copiado para a área de transferência! Abra este link no dispositivo móvel para parear.');
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const mapWrapper = document.getElementById('overview-map-wrapper');
      if (!mapWrapper) return;
      const rect = mapWrapper.getBoundingClientRect();
      const newHeight = Math.max(120, Math.min(600, e.clientY - rect.top));
      setMapHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      localStorage.setItem('ac_overview_map_height', String(mapHeight));
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, mapHeight]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totais = overviewData?.totais ?? {};
  const escolas = overviewData?.escolas ?? [];

  // Prepara os pontos de escolas e residências de alunos para o UnifiedMapView
  const dataPoints = useMemo(() => {
    const points: any[] = [];
    escolas.forEach((esc: any) => {
      points.push({
        id: `school-${esc.id}`,
        type: 'SCHOOL' as const,
        lat: Number(esc.latitude),
        lng: Number(esc.longitude),
        label: esc.nome,
        sublabel: `${esc.tipoRede} · ${esc.cidade}/${esc.uf}`
      });

      const totalAlunos = esc._count?.alunos ?? 0;
      for (let i = 0; i < totalAlunos; i++) {
        const angle = (i / totalAlunos) * 2 * Math.PI;
        const r = 0.003 + Math.sin(i * 17) * 0.009;
        const lat = Number(esc.latitude)  + Math.cos(angle) * r + Math.sin(i * 7)  * 0.004;
        const lng = Number(esc.longitude) + Math.sin(angle) * r + Math.cos(i * 13) * 0.004;

        // Distribuição sintética de status de auditoria
        const hasTag = (i % 3 !== 0);
        const hasApp = (i % 4 !== 0);

        points.push({
          id: `student-${esc.id}-${i}`,
          type: 'STUDENT_HOME' as const,
          lat,
          lng,
          label: `Residência Aluno #${i + 1} (${esc.nome})`,
          payload: { hasTag, hasApp }
        });
      }
    });
    return points;
  }, [escolas]);

  if (schoolIds.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center h-full text-center p-8 ${isLight ? 'text-slate-400' : 'text-[#a6adc8]/40'}`}>
        <span className="text-4xl mb-3">🏢</span>
        <div className="text-sm font-bold">Nenhuma escola selecionada</div>
        <div className="text-xs mt-1">Marque uma ou mais escolas à esquerda para ver a Visão Geral.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex flex-col items-center justify-center h-full gap-2 text-xs py-12 ${isLight ? 'text-slate-400' : 'text-[#a6adc8]/50'}`}>
        <div className="w-6 h-6 border-2 border-[#74c7ec] border-t-transparent rounded-full animate-spin" />
        Carregando painel de visão geral...
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-full overflow-hidden">
      
      {/* Coluna da Esquerda: Mapa Consolidado */}
      <div className="w-full lg:w-[45%] flex flex-col border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-white/8 p-4 flex-shrink-0 lg:flex-shrink">
        <div className="flex items-center gap-2 mb-2 flex-shrink-0">
          <div className="h-1.5 w-1.5 rounded-full bg-[#74c7ec]" />
          <span className={`text-xs font-semibold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-[#74c7ec]'}`}>Localização das Escolas</span>
        </div>
        <div className="flex-1 relative flex flex-col min-h-[280px] lg:min-h-0">
          <div className="absolute inset-0">
            <UnifiedMapView
              dataPoints={dataPoints}
              config={{
                theme,
                useClusteringByDefault: true,
                enableToolbar: true,
              }}
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
      </div>

      {/* Coluna da Direita: Métricas e Atores de Monitoramento */}
      <div className="flex-1 overflow-y-auto p-4 min-h-0 flex flex-col gap-5">
        
        {/* 📊 TOTAIS AGREGADOS */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-[#89b4fa]" />
            <span className={`text-xs font-semibold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-[#89b4fa]'}`}>Métricas Consolidadas ({escolas.length} Escolas)</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className={`border rounded-xl p-2.5 text-center flex flex-col items-center ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/3 border border-white/8'}`}>
              <span className="text-base mb-1">🎓</span>
              <span className={`text-sm font-extrabold leading-none ${isLight ? 'text-blue-600' : 'text-[#74c7ec]'}`}>{totais.alunos}</span>
              <span className={`text-[10px] uppercase tracking-wider mt-1.5 leading-none ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Alunos</span>
            </div>
            <div className={`border rounded-xl p-2.5 text-center flex flex-col items-center ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/3 border border-white/8'}`}>
              <span className="text-base mb-1">📚</span>
              <span className={`text-sm font-extrabold leading-none ${isLight ? 'text-cyan-600' : 'text-[#89dceb]'}`}>{totais.turmas}</span>
              <span className={`text-[10px] uppercase tracking-wider mt-1.5 leading-none ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Turmas</span>
            </div>
            <div className={`border rounded-xl p-2.5 text-center flex flex-col items-center ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/3 border border-white/8'}`}>
              <span className="text-base mb-1">👨‍🏫</span>
              <span className={`text-sm font-extrabold leading-none ${isLight ? 'text-indigo-600' : 'text-[#89b4fa]'}`}>{totais.professores}</span>
              <span className={`text-[10px] uppercase tracking-wider mt-1.5 leading-none ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Professores</span>
            </div>
            <div className={`border rounded-xl p-2.5 text-center flex flex-col items-center ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/3 border border-white/8'}`}>
              <span className="text-base mb-1">🔑</span>
              <span className={`text-sm font-extrabold leading-none ${isLight ? 'text-amber-600' : 'text-[#fab387]'}`}>{totais.chaveiros}</span>
              <span className={`text-[10px] uppercase tracking-wider mt-1.5 leading-none ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Chaveiros</span>
            </div>
            <div className={`border rounded-xl p-2.5 text-center flex flex-col items-center ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/3 border border-white/8'}`}>
              <span className="text-base mb-1">👨‍👩‍👧</span>
              <span className={`text-sm font-extrabold leading-none ${isLight ? 'text-violet-600' : 'text-[#cba6f7]'}`}>{totais.responsaveis}</span>
              <span className={`text-[10px] uppercase tracking-wider mt-1.5 leading-none ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Responsáveis</span>
            </div>
            <div className={`border rounded-xl p-2.5 text-center flex flex-col items-center ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/3 border border-white/8'}`}>
              <span className="text-base mb-1">📱</span>
              <span className={`text-sm font-extrabold leading-none ${isLight ? 'text-emerald-600' : 'text-[#a6e3a1]'}`}>{totais.appInstalado}</span>
              <span className={`text-[10px] uppercase tracking-wider mt-1.5 leading-none ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Com App</span>
            </div>
            <div className={`border rounded-xl p-2.5 text-center flex flex-col items-center ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/3 border border-white/8'}`}>
              <span className="text-base mb-1">📡</span>
              <span className={`text-sm font-extrabold leading-none ${isLight ? 'text-pink-600' : 'text-[#f5c2e7]'}`}>{totais.dispositivo}</span>
              <span className={`text-[10px] uppercase tracking-wider mt-1.5 leading-none ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Leitores RFID</span>
            </div>
            <div className={`border rounded-xl p-2.5 text-center flex flex-col items-center ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-white/3 border border-white/8'}`}>
              <span className="text-base mb-1">🏙️</span>
              <span className={`text-sm font-extrabold leading-none ${isLight ? 'text-teal-600' : 'text-[#94e2d5]'}`}>{totais.municipio}</span>
              <span className={`text-[10px] uppercase tracking-wider mt-1.5 leading-none ${isLight ? 'text-slate-400' : 'text-[#6c7086]'}`}>Município</span>
            </div>
          </div>
        </div>

        {/* 👥 ATORES FIXOS DE MONITORAMENTO */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-[#cba6f7]" />
            <span className={`text-xs font-semibold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-[#cba6f7]'}`}>Atores Fixos de Monitoramento</span>
          </div>

          <div className="flex flex-col gap-3">
            {/* PROFESSORES FIXOS */}
            <div>
              <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${isLight ? 'text-indigo-600' : 'text-[#89b4fa]'}`}>👨‍🏫 Professores Compartilhados (Cross-School)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {fixedActors?.professores?.map((prof: any) => {
                  const isFem = prof.genero === 'FEMININO';
                  const fallback = isFem ? '/img/avatars/f/01.jpg' : '/img/avatars/m/01.jpg';
                  const isActionLoading = actionLoadingId === prof.id;
                  return (
                    <div key={prof.id} className={`rounded-xl border p-2.5 flex items-start gap-3 transition-all ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'border-white/8 bg-white/2'}`}>
                      <img src={prof.fotoUrl || fallback} alt={prof.nome} className={`w-10 h-10 rounded-full object-cover border ${isLight ? 'border-slate-200' : 'border-white/10'}`}
                           onError={e => { (e.target as HTMLImageElement).src = fallback; }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-bold leading-tight ${isLight ? 'text-slate-800' : 'text-white'}`}>{prof.nome}</span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                                style={{ 
                                  background: prof.pushConfigured ? (isLight ? '#d1fae5' : '#a6e3a122') : (isLight ? '#fee2e2' : '#f38ba822'), 
                                  color: prof.pushConfigured ? (isLight ? '#065f46' : '#a6e3a1') : (isLight ? '#991b1b' : '#f38ba8') 
                                }}>
                            {prof.pushConfigured ? '🟢 PUSH REAL' : '🔴 SEM DEVICE'}
                          </span>
                        </div>
                        <div className={`text-[10px] mt-0.5 font-mono ${isLight ? 'text-slate-500' : 'text-[#6c7086]'}`}>{prof.email}</div>
                        
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {prof.escolas?.filter(Boolean).map((esc: any, idx: number) => (
                            <span key={esc.id ?? idx} className={`text-[9px] px-1.5 py-0.5 rounded-md ${isLight ? 'bg-slate-100 text-slate-700 border border-slate-200' : 'bg-[#89b4fa]/15 text-[#89b4fa]'}`}>
                              🏫 {esc.nome ?? 'Sem Nome'} ({esc.cidade ?? '?'})
                            </span>
                          ))}
                        </div>

                        {/* Ações do Professor */}
                        <div className={`flex items-center gap-1.5 mt-2 pt-2 border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`}>
                          {prof.pushConfigured ? (
                            <>
                              <button
                                onClick={() => handleSendPush(prof.id)}
                                disabled={isActionLoading}
                                className={`text-[10px] font-bold px-2 py-1 rounded transition-all disabled:opacity-50 ${
                                  isLight 
                                    ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200' 
                                    : 'bg-[#a6e3a1]/10 text-[#a6e3a1] hover:bg-[#a6e3a1]/20'
                                }`}
                              >
                                {isActionLoading ? 'Enviando...' : '⚡ Enviar Push de Teste'}
                              </button>
                              <button
                                onClick={() => handleDisconnect(prof.id)}
                                disabled={isActionLoading}
                                className={`text-[10px] font-bold px-2 py-1 rounded transition-all disabled:opacity-50 ${
                                  isLight 
                                    ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200' 
                                    : 'bg-[#f38ba8]/10 text-[#f38ba8] hover:bg-[#f38ba8]/20'
                                }`}
                              >
                                {isActionLoading ? 'Aguarde...' : '🔌 Desconectar'}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => setActiveActorQr({ id: prof.id, nome: prof.nome, type: 'PROFESSOR' })}
                                className={`text-[10px] font-bold px-2 py-1 rounded transition-all ${
                                  isLight 
                                    ? 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200' 
                                    : 'bg-[#89b4fa]/10 text-[#89b4fa] hover:bg-[#89b4fa]/20'
                                }`}
                              >
                                📱 Conectar Aparelho
                              </button>
                              <button
                                onClick={() => handleCopyLink(prof.id)}
                                className={`text-[10px] font-bold px-2 py-1 rounded transition-all ${
                                  isLight 
                                    ? 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200' 
                                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                                }`}
                              >
                                📋 Copiar Link
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(!fixedActors?.professores || fixedActors.professores.length === 0) && (
                  <div className={`text-xs py-1 ${isLight ? 'text-slate-400' : 'text-[#45475a]'}`}>Nenhum professor fixo cadastrado. Execute o mega-seed.</div>
                )}
              </div>
            </div>

            {/* RESPONSÁVEIS FIXOS */}
            <div>
              <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${isLight ? 'text-violet-600' : 'text-[#cba6f7]'}`}>👨‍👩‍👧 Responsáveis com Alunos em Várias Escolas (Irmãos)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {fixedActors?.responsaveis?.map((resp: any) => {
                  const hasPush = resp.dispositivosPush && resp.dispositivosPush.length > 0;
                  const platform = hasPush ? resp.dispositivosPush[0].plataforma : '';
                  const fallback = '/img/avatars/f/03.jpg';
                  const isActionLoading = actionLoadingId === resp.id;
                  return (
                    <div key={resp.id} className={`rounded-xl border p-2.5 flex items-start gap-3 transition-all ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'border-white/8 bg-white/2'}`}>
                      <img src={resp.fotoUrl || fallback} alt={resp.nome} className={`w-10 h-10 rounded-full object-cover border ${isLight ? 'border-slate-200' : 'border-white/10'}`}
                           onError={e => { (e.target as HTMLImageElement).src = fallback; }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-bold leading-tight ${isLight ? 'text-slate-800' : 'text-white'}`}>{resp.nome}</span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                                style={{ 
                                  background: hasPush ? (isLight ? '#d1fae5' : '#a6e3a122') : (isLight ? '#fee2e2' : '#f38ba822'), 
                                  color: hasPush ? (isLight ? '#065f46' : '#a6e3a1') : (isLight ? '#991b1b' : '#f38ba8') 
                                }}>
                            {hasPush ? `🟢 PUSH REAL (${platform})` : '🔴 SEM DEVICE'}
                          </span>
                        </div>
                        <div className={`text-[10px] mt-0.5 font-mono ${isLight ? 'text-slate-500' : 'text-[#6c7086]'}`}>{resp.email} · {resp.telefone}</div>
                        
                        <div className={`flex flex-col gap-1 mt-1.5 border-t pt-1.5 ${isLight ? 'border-slate-100' : 'border-white/4'}`}>
                          <span className={`text-[9px] font-bold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-[#6c7086]'}`}>Filhos / Irmãos Vinculados:</span>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {resp.alunos?.filter(Boolean).map((al: any, idx: number) => (
                              <span key={al.id ?? idx} className={`text-[9px] px-1.5 py-0.5 rounded-md ${isLight ? 'bg-violet-50 text-violet-700 border border-violet-100' : 'bg-[#cba6f7]/15 text-[#cba6f7]'}`}>
                                🎓 {al.nome ?? 'Sem Nome'} · 🏫 {al.escola?.nome ?? '?'}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Ações do Responsável */}
                        <div className={`flex items-center gap-1.5 mt-2 pt-2 border-t ${isLight ? 'border-slate-100' : 'border-white/5'}`}>
                          {hasPush ? (
                            <>
                              <button
                                onClick={() => handleSendPush(resp.id)}
                                disabled={isActionLoading}
                                className={`text-[10px] font-bold px-2 py-1 rounded transition-all disabled:opacity-50 ${
                                  isLight 
                                    ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200' 
                                    : 'bg-[#a6e3a1]/10 text-[#a6e3a1] hover:bg-[#a6e3a1]/20'
                                }`}
                              >
                                {isActionLoading ? 'Enviando...' : '⚡ Enviar Push de Teste'}
                              </button>
                              <button
                                onClick={() => handleDisconnect(resp.id)}
                                disabled={isActionLoading}
                                className={`text-[10px] font-bold px-2 py-1 rounded transition-all disabled:opacity-50 ${
                                  isLight 
                                    ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200' 
                                    : 'bg-[#f38ba8]/10 text-[#f38ba8] hover:bg-[#f38ba8]/20'
                                }`}
                              >
                                {isActionLoading ? 'Aguarde...' : '🔌 Desconectar'}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => setActiveActorQr({ id: resp.id, nome: resp.nome, type: 'RESPONSAVEL' })}
                                className={`text-[10px] font-bold px-2 py-1 rounded transition-all ${
                                  isLight 
                                    ? 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200' 
                                    : 'bg-[#89b4fa]/10 text-[#89b4fa] hover:bg-[#89b4fa]/20'
                                }`}
                              >
                                📱 Conectar Aparelho
                              </button>
                              <button
                                onClick={() => handleCopyLink(resp.id)}
                                className={`text-[10px] font-bold px-2 py-1 rounded transition-all ${
                                  isLight 
                                    ? 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200' 
                                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                                }`}
                              >
                                📋 Copiar Link
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(!fixedActors?.responsaveis || fixedActors.responsaveis.length === 0) && (
                  <div className={`text-xs py-1 ${isLight ? 'text-slate-400' : 'text-[#45475a]'}`}>Nenhum responsável fixo cadastrado. Execute o mega-seed.</div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>

      {activeActorQr && focusedSessionId && (
        <SimActorPairingQrModal
          actorId={activeActorQr.id}
          actorNome={activeActorQr.nome}
          sessionId={focusedSessionId}
          roleLabel={activeActorQr.type}
          onClose={() => setActiveActorQr(null)}
        />
      )}
      {activeActorQr && !focusedSessionId && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className={`border rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl relative ${
            isLight ? 'bg-white border-slate-200' : 'bg-[#1e1e2e] border border-white/10'
          }`}>
            <button
              onClick={() => setActiveActorQr(null)}
              className={`absolute top-3 right-3 transition-all text-sm font-bold bg-transparent border-none cursor-pointer ${
                isLight ? 'text-slate-400 hover:text-slate-700' : 'text-[#a6adc8] hover:text-white'
              }`}
            >
              ✕
            </button>
            <div className={`text-sm font-bold mb-1 ${isLight ? 'text-slate-800' : 'text-white'}`}>
              Pareamento — selecione uma sessão RUNNING
            </div>
            <div className={`text-xs font-mono mb-4 ${isLight ? 'text-blue-600' : 'text-[#89b4fa]'}`}>{activeActorQr.nome}</div>
            <p className={`text-xs leading-normal px-2 mb-4 ${isLight ? 'text-slate-600' : 'text-[#a6adc8]'}`}>
              Foque uma sessão de simulação ativa no cockpit para gerar o QR com relógio compartilhado.
            </p>
            <button
              onClick={() => {
                handleCopyLink(activeActorQr.id);
                setActiveActorQr(null);
              }}
              className={`text-xs font-bold w-full py-2.5 rounded-xl transition-all border ${
                isLight
                  ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300'
                  : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
              }`}
            >
              Copiar link legado (sem sessão)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
